import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  hasPermission, 
  hasPermissions, 
  getRolePermissions,
  RolePermissions,
  clearPermissionsCache 
} from '../utils/permissions';

export const usePermissions = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<RolePermissions | null>(null);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    // Wait for auth to finish loading before trying to load permissions
    if (authLoading) {
      console.log('usePermissions: Waiting for auth to finish loading...');
      return;
    }

    // Use userRole.name instead of role (which is now role_id)
    const userRoleName = user?.userRole?.name;
    
    if (!userRoleName) {
      console.log('usePermissions: No user role available, setting permissions to null');
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      console.log('usePermissions: Loading permissions for role:', userRoleName);
      setLoading(true);
      const rolePermissions = await getRolePermissions(userRoleName);
      console.log('usePermissions: Loaded permissions:', rolePermissions);
      setPermissions(rolePermissions);
    } catch (error) {
      console.error('usePermissions: Error loading permissions:', error);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [user?.userRole?.name, authLoading]);

  useEffect(() => {
    console.log('usePermissions useEffect triggered:', {
      authLoading,
      userRole: user?.userRole?.name,
      hasUser: !!user
    });
    loadPermissions();
  }, [loadPermissions]);

  const checkPermission = useCallback(async (
    resource: keyof RolePermissions, 
    action: 'read' | 'write' | 'delete'
  ): Promise<boolean> => {
    const userRoleName = user?.userRole?.name;
    if (!userRoleName) return false;
    return await hasPermission(userRoleName, resource, action);
  }, [user?.userRole?.name]);

  const checkPermissions = useCallback(async (
    permissions: Array<{ resource: keyof RolePermissions; action: 'read' | 'write' | 'delete' }>
  ): Promise<boolean> => {
    const userRoleName = user?.userRole?.name;
    if (!userRoleName) return false;
    return await hasPermissions(userRoleName, permissions);
  }, [user?.userRole?.name]);

  const refreshPermissions = useCallback(() => {
    clearPermissionsCache();
    loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    loading,
    checkPermission,
    checkPermissions,
    refreshPermissions,
    userRole: user?.userRole?.name
  };
}; 